// #region imports

// --- Angular
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// --- RxJs
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgbModal, NgbAlert } from '@ng-bootstrap/ng-bootstrap';

// --- HS Code
import { GenericValidator } from '../../../shared/validators/generic-validator';
import { CompareValidator } from '../../../shared/validators/compare-validator';
import { environment } from '../../../../environments/environment';
import {
  EmailAddressValidationModel,
  RegistrationModel,
  Constants
} from '../../../models';
import { IsbnNumberModalComponent } from '../isbn-number-modal/isbn-number-modal.component';
// #endregion

@Component({
  selector: 'hs-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class RegisterComponent implements OnInit, OnChanges, OnDestroy {
  displayMessage: { [key: string]: string } = {};
  private validationMessages: { [key: string]: { [key: string]: string } };
  private genericValidator: GenericValidator;

  private _emailAddressUpdated: Subject<string>;

  @Input() pageTitle = '';
  @Input() errorMessage: string;
  @Input() isRegistering: boolean;
  @Input() isRegistered: boolean;
  @Input() emailAddressValidation: EmailAddressValidationModel;
  @Input() isValidatingEmailAddress: boolean;

  @Input() validationMessageFirstNameRequired: string;
  @Input() validationMessageFirstNameMaxLength: string;
  @Input() validationMessageLastNameRequired: string;
  @Input() validationMessageLastNameMaxLength: string;
  @Input() validationMessagePasswordRequired: string;
  @Input() validationMessagePasswordMinLength: string;
  @Input() validationMessagePasswordMaxLength: string;
  @Input() validationMessagePasswordComplexity: string;
  @Input() validationMessageConfirmPasswordRequired: string;
  @Input() validationMessageConfirmPasswordMatch: string;
  @Input() validationMessageEmailAddressRequired: string;
  @Input() validationMessageEmailAddressMaxLength: string;
  @Input() validationMessageEmailAddressInvalid: string;
  @Input() validationMessageIsbnNumberRequired: string;
  @Input() validationMessageInValidIsbnNumber: string;
  @Input() validationMessageIsbnNumberMaxLength: string;
  @Input() validationMessageUserAgreementRequired: string;

  @Output() register = new EventEmitter<RegistrationModel>();
  @Output() cancel = new EventEmitter();
  @Output() emailAddressChanged = new EventEmitter<string>();

  siteKey = environment.siteKey;

  emailAddressChangedSubscription: Subscription;

  emailAddressRequiredPlaceholder: string;
  size = 'normal';
  lang = 'en';
  theme = 'light';
  type = 'image';

  userAgreementLinks = {
    privacyLink: Constants.PRIVACY_POLICY_LINK,
    termsLink: Constants.TERMS_OF_AGREEMENT_LINK
  };

  fieldValueVisibility = {
    password: false,
    confirmPassword: false
  };

  componentActive = true;
  registerForm: FormGroup;
  private _isFormInitialised = false;

  isMobile: boolean;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private deviceService: DeviceDetectorService,
    private modalService: NgbModal
  ) {
    this.isRegistering = false;
    this._emailAddressUpdated = new Subject<string>();

    this.validationMessageFirstNameRequired = 'First Name is required.';
    this.validationMessageFirstNameMaxLength =
      'First Name cannot exceed 100 characters.';
    this.validationMessageLastNameRequired = 'Last Name is required.';
    this.validationMessageLastNameMaxLength =
      'Last Name cannot exceed 100 characters.';
    this.validationMessagePasswordRequired = 'Password is required.';
    this.validationMessagePasswordMinLength =
      'Password must be longer than 6 characters.';
    this.validationMessagePasswordMaxLength =
      'Password cannot exceed 100 characters.';
    this.validationMessagePasswordComplexity =
      'Password must meet complexity requirements';
    this.validationMessageConfirmPasswordRequired =
      'Confirm password is required';
    this.validationMessageConfirmPasswordMatch =
      'Confirm password does not match';
    this.validationMessageEmailAddressRequired = 'Email Address is required.';
    this.validationMessageEmailAddressMaxLength =
      'Email Address cannot exceed 256 characters.';
    this.validationMessageEmailAddressInvalid =
      'You must supply a valid Email Address.';
    this.validationMessageIsbnNumberRequired = 'ISBN Number is required.';
    this.validationMessageInValidIsbnNumber = 'ISBN Number is in-correct.';
    this.validationMessageIsbnNumberMaxLength =
      'ISBN Number cannot exceed 100 characters';
    this.validationMessageUserAgreementRequired =
      'Please accept the privacy policy and terms of agreement';
  }

  ngOnInit() {
    this.emailAddressChangedSubscription = this._emailAddressUpdated
      .asObservable()
      .pipe(
        debounceTime(600),
        distinctUntilChanged()
      )
      .subscribe(emailAddress => {
        this.emailAddressChanged.emit(emailAddress);
      });

    this.validationMessages = {
      firstName: {
        required: this.validationMessageFirstNameRequired,
        maxlength: this.validationMessageFirstNameMaxLength
      },
      lastName: {
        required: this.validationMessageLastNameRequired,
        maxlength: this.validationMessageLastNameMaxLength
      },
      password: {
        required: this.validationMessagePasswordRequired,
        minlength: this.validationMessagePasswordMinLength,
        maxlength: this.validationMessagePasswordMaxLength,
        pattern: this.validationMessagePasswordComplexity
      },
      confirmPassword: {
        required: this.validationMessageConfirmPasswordRequired,
        isEqual: this.validationMessageConfirmPasswordMatch
      },
      emailAddress: {
        required: this.validationMessageEmailAddressRequired,
        maxlength: this.validationMessageEmailAddressMaxLength,
        email: this.validationMessageEmailAddressInvalid
      },
      isbnNumber: {
        required: this.validationMessageIsbnNumberRequired,
        maxlength: this.validationMessageIsbnNumberMaxLength,
        validateISBN: this.validationMessageInValidIsbnNumber
      },
      userAgreement: {
        required: this.validationMessageUserAgreementRequired
      },
    };

    this.genericValidator = new GenericValidator(this.validationMessages);
    this._initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (
      changes.validationMessageFirstNameRequired &&
      changes.validationMessageFirstNameRequired.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.firstName.required =
        changes.validationMessageFirstNameRequired.currentValue;
    }
    if (
      changes.validationMessageFirstNameMaxLength &&
      changes.validationMessageFirstNameMaxLength.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.firstName.maxlength =
        changes.validationMessageFirstNameMaxLength.currentValue;
    }

    if (
      changes.validationMessageLastNameRequired &&
      changes.validationMessageLastNameRequired.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.lastName.required =
        changes.validationMessageLastNameRequired.currentValue;
    }
    if (
      changes.validationMessageLastNameMaxLength &&
      changes.validationMessageLastNameMaxLength.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.lastName.maxlength =
        changes.validationMessageLastNameMaxLength.currentValue;
    }

    if (
      changes.validationMessagePasswordRequired &&
      changes.validationMessagePasswordRequired.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.password.required =
        changes.validationMessagePasswordRequired.currentValue;
    }
    if (
      changes.validationMessagesPasswordMinLength &&
      changes.validationMessagesPasswordMinLength.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.password.minLength =
        changes.validationMessagesPasswordMinLength.currentValue;
    }
    if (
      changes.validationMessagesPasswordMaxLength &&
      changes.validationMessagesPasswordMaxLength.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.password.maxlength =
        changes.validationMessagesPasswordMaxLength.currentValue;
    }
    if (
      changes.validationMessagePasswordComplexity &&
      changes.validationMessagePasswordComplexity.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.password.pattern =
        changes.validationMessagePasswordComplexity.currentValue;
    }

    if (
      changes.validationMessageConfirmPasswordRequired &&
      changes.validationMessageConfirmPasswordRequired.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.confirmPassword.required =
        changes.validationMessageConfirmPasswordRequired.currentValue;
    }
    if (
      changes.validationMessageConfirmPasswordMatch &&
      changes.validationMessageConfirmPasswordMatch.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.confirmPassword.isEqual =
        changes.validationMessageConfirmPasswordMatch.currentValue;
    }

    if (
      changes.validationMessageEmailAddressRequired &&
      changes.validationMessageEmailAddressRequired.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.emailAddress.required =
        changes.validationMessageEmailAddressRequired.currentValue;
    }
    if (
      changes.validationMessagesEmailAddressMaxLength &&
      changes.validationMessagesEmailAddressMaxLength.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.emailAddress.maxlength =
        changes.validationMessagesEmailAddressMaxLength.currentValue;
    }
    if (
      changes.validationMessageEmailAddressInvalid &&
      changes.validationMessageEmailAddressInvalid.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.emailAddress.email =
        changes.validationMessageEmailAddressInvalid.currentValue;
    }

    if (
      changes.validationMessageIsbnNumberRequired &&
      changes.validationMessageIsbnNumberRequired.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.isbnNumber.required =
        changes.validationMessageIsbnNumberRequired.currentValue;
    }
    if (
      changes.validationMessageInValidIsbnNumber &&
      changes.validationMessageInValidIsbnNumber.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.isbnNumber.validateISBN =
        changes.validationMessageInValidIsbnNumber.currentValue;
    }
    if (
      changes.validationMessageIsbnNumberMaxLength &&
      changes.validationMessageIsbnNumberMaxLength.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.isbnNumber.maxlength =
        changes.validationMessageIsbnNumberMaxLength.currentValue;
    }

    if (
      changes.validationMessageUserAgreementRequired &&
      changes.validationMessageUserAgreementRequired.currentValue &&
      this.validationMessages
    ) {
      this.genericValidator.validationMessages.userAgreement.required =
        changes.validationMessageUserAgreementRequired.currentValue;
    }
  }

  private _initForm(): void {
    this.registerForm = this.fb.group(
      {
        firstName: ['', [Validators.required, Validators.maxLength(100)]],
        lastName: ['', [Validators.required, Validators.maxLength(100)]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(100),
            Validators.pattern('(?=(.*[A-Za-z]){1,})(?=(.*[\\d]){1,}).{1,}$')
          ]
        ],
        confirmPassword: '',
        emailAddress: [
          '',
          [Validators.required, Validators.maxLength(100), Validators.email]
        ],
        isbnNumber: ['', [Validators.required, Validators.maxLength(100)]],
        userAgreement: [false, Validators.requiredTrue],
        newsletterOptIn: [false]
      },
      { validator: CompareValidator('eq', 'password', 'confirmPassword') }
    );
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnDestroy(): void {
    this.componentActive = false;
    this.emailAddressChangedSubscription.unsubscribe();
    this.emailAddressChangedSubscription = null;
  }

  onEmailAddressChanged(event: any): void {
    if (this.registerForm.controls['emailAddress'].valid) {
      this._emailAddressUpdated.next(
        this.registerForm.get('emailAddress').value
      );
      this.displayMessage = this.genericValidator.processMessages(
        this.registerForm
      );
    } else {
      this.displayMessage = this.genericValidator.processMessages(
        this.registerForm
      );
    }
  }

  blur(): void {
    this.displayMessage = this.genericValidator.processMessages(
      this.registerForm
    );
  }

  cancelRegistration(): void {
    this.cancel.emit();
  }

  onRegister(): void {
    this.displayMessage = this.genericValidator.processMessages(
      this.registerForm,
      true
    );

    if (!this.registerForm.valid) {
      this.errorMessage = this.translate.instant('validationErrorsMessage');
      this.isRegistering = false;
      return;
    }

    if (!this.registerForm.dirty) {
      return;
    }

    const p = { ...this.registerForm.value };
    this.register.emit(p);
  }

  toggleFieldValueVisibility(fieldKey: string) {
    if (typeof this.fieldValueVisibility[fieldKey] === 'undefined') {
      return;
    }
    this.fieldValueVisibility[fieldKey] = !this.fieldValueVisibility[fieldKey];

    if (fieldKey === 'password') {
      if (this.fieldValueVisibility[fieldKey]) {
        this.registerForm.get('confirmPassword').disable();
      } else {
        this.registerForm.get('confirmPassword').enable();
      }
      this.blur();
    }
  }
  openIsbnNumberModal() {
    this.modalService.open(IsbnNumberModalComponent, { centered: true });
  }

  handleReset(): void {}

  handleExpire(): void {}

  handleLoad(): void {}

  handleSuccess(event: any): void {}

}
